import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from "app/hooks";
import { resetLeftMenuJobSeekerJoinedProgramsState } from 'features/left-menu-job-seeker-joined-programs';
import { GetJobSeekerProgramConnectionsTable } from 'features/job-seeker-explore-programs/types';
import { programsAPI } from 'api';
import AsyncList from 'components/AsyncList';
import { SelectedProgramContextTab, getProgramContextTabRouteParam } from 'features/selected-context';
import { selectJobSeeker } from 'features/job-seeker-auth';
import { NavLink } from 'react-router-dom';
import { ROUTES } from 'app/routes';
import NavCollapse from 'components/NavCollapse';
import { checkIsAtLeastOneProgramLead } from 'utils/job-seeker-on-behalf-of-organization-helper';
import { selectJobSeekerOnBehalfOfOrganizationAdminId } from "features/job-seeker-on-behalf-of-organization-admin";
import ShowForUserOrMemberRoles from 'components/ShowForUserOrMemberRoles';
import { Roles, MemberRoles } from 'features/types';
import CircularLogo from 'components/CircularLogo';
import SvgIcon from 'components/SvgIcon';
import './AppNavbar.scss';

type MenuState = {
  [key: string]: Record<string, any>;
};

const getLeftMenuState = (): MenuState => {
  const defaultState: MenuState = {
    jobSeekerJoinedPrograms: {
      isOpen: true,
    }
  };
  let storedState: MenuState = {};
  try {
    const raw = localStorage.getItem('left-menu-state');
    if (raw) storedState = JSON.parse(raw);
  } catch (error) { }
  const mergedState: MenuState = {};
  for (const key in defaultState) {
    if (storedState[key] && typeof storedState[key] === 'object') {
      mergedState[key] = { ...defaultState[key], ...storedState[key] };
    } else {
      mergedState[key] = defaultState[key];
    }
  }
  return mergedState;
};

const setLeftMenuState = (item: string, key: string, value: string | boolean | number) => {
  try {
    const leftMenuState = getLeftMenuState();
    if (!leftMenuState[item]) leftMenuState[item] = {};
    leftMenuState[item][key] = value;
    localStorage.setItem('left-menu-state', JSON.stringify(leftMenuState));
  } catch (error) { }
}

type Props = {
  sidebarOpen: boolean;
  closeSidebar(): void;
};

const JobSeekerAppNavbar = ({ sidebarOpen, closeSidebar }: Props) => {
  const dispatch = useAppDispatch();
  const jobSeeker = useAppSelector(selectJobSeeker);
  const jobSeekerCanPostJobsPermissions = jobSeeker?.groupPermissions && (jobSeeker.groupPermissions.totalGroupsAllowedToPost > 0 || jobSeeker.groupPermissions.atLeastOnEnablePromoteJobPostsToNetworks === true);
  const isProgramOrCommunityLead = checkIsAtLeastOneProgramLead(jobSeeker);
  const onBehalfOfOrganizationAdminId = useAppSelector(selectJobSeekerOnBehalfOfOrganizationAdminId);
  const isNetworkManager = true;
  const memberNavSettings = jobSeeker?.memberNavSettings;

  useEffect(() => {
    return () => {
      dispatch(resetLeftMenuJobSeekerJoinedProgramsState());
    };
  }, [dispatch]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const getLeftMenuJobSeekerJoinedPrograms = (params: GetJobSeekerProgramConnectionsTable) => {
    const paramsWithTab = { ...params, filters: { ...params.filters, tab: 'MyGroups' } };
    return programsAPI.getJobSeekerTable(paramsWithTab);
  };

  const selectedProgramContextTabParam = getProgramContextTabRouteParam(SelectedProgramContextTab.GroupHub);
  const leftMenuState = getLeftMenuState();

  return (
    <div ref={wrapperRef} className={`app-sidebar ${sidebarOpen ? 'opened' : ''}`}>
      <nav className="sidebar-nav">
        <ul>
          {memberNavSettings?.communityHub !== false && (
            <li>
              <NavLink exact to={ROUTES.JOB_SEEKER.RESOURCES} onClick={() => closeSidebar()} >
                <span className="menu-link-title"><SvgIcon name="sidebar/resources" />Community Hub</span>
              </NavLink>
            </li>
          )}
          {memberNavSettings?.jobs !== false && (
            <li>
              <NavCollapse routes={[ROUTES.JOB_SEEKER.OPPORTUNITIES, ROUTES.JOB_SEEKER.JOB_POSTS, ROUTES.JOB_SEEKER.JOB_RESPONSES]} name="Jobs" svgIcon="sidebar/job-templates">
                <>
                  <NavLink className="nav-collapse-link" to={ROUTES.JOB_SEEKER.OPPORTUNITIES} onClick={() => closeSidebar()} >
                    <span className="menu-link-title">Find Jobs</span>
                  </NavLink>
                  {jobSeekerCanPostJobsPermissions && <>
                    <ShowForUserOrMemberRoles
                      roles={[MemberRoles.FullAdmin, MemberRoles.Member]}
                      validateOrgnizationContext={true}
                    >
                      <NavLink className="nav-collapse-link" to={ROUTES.JOB_SEEKER.JOB_POSTS} onClick={() => closeSidebar()} >
                        <span className="menu-link-title">My job posts</span>
                      </NavLink>
                      <NavLink className="nav-collapse-link" to={ROUTES.JOB_SEEKER.JOB_RESPONSES} onClick={() => closeSidebar()} >
                        <span className="menu-link-title">My job applicants</span>
                      </NavLink>
                    </ShowForUserOrMemberRoles>
                  </>}
                </>
              </NavCollapse>
            </li>
          )}
          {memberNavSettings?.productsAndServices !== false && (
            <li>
              <NavLink exact to={ROUTES.JOB_SEEKER.RESOURCES_PRODUCTS_SERVICES} onClick={() => closeSidebar()} >
                <span className="menu-link-title"><SvgIcon name="sidebar/agreement" />Products & Services</span>
              </NavLink>
            </li>
          )}
          {memberNavSettings?.events !== false && (
            <li>
              <NavLink exact to={ROUTES.JOB_SEEKER.RESOURCES_EVENTS} onClick={() => closeSidebar()} >
                <span className="menu-link-title"><SvgIcon name="sidebar/calendar-menu" />Events</span>
              </NavLink>
            </li>
          )}
          {memberNavSettings?.members !== false && (
            <li>
              <NavLink exact to={ROUTES.JOB_SEEKER.JOB_SEEKERS} onClick={() => closeSidebar()} >
                <span className="menu-link-title"><SvgIcon name="sidebar/job-seekers" />Members</span>
              </NavLink>
            </li>
          )}
          {memberNavSettings?.groups !== false && (
            <li>
              <NavCollapse className="sub-menu-list" routes={[ROUTES.JOB_SEEKER.PROGRAM_CONTEXT.BASE]} name="My groups" svgIcon="sidebar/programs-joined" autoOpenClose={leftMenuState?.jobSeekerJoinedPrograms?.isOpen === true} onOpenChange={(isOpen: boolean) => { setLeftMenuState('jobSeekerJoinedPrograms', 'isOpen', isOpen); }}>
                <AsyncList
                  scrollContainerRef={wrapperRef}
                  endElementRef={endRef}
                  getSelectListFunc={getLeftMenuJobSeekerJoinedPrograms}
                  renderItem={(row) => (
                    <NavLink className="nav-collapse-link sub-menu-list-item" to={`/job-seeker/program/${row.id}/${selectedProgramContextTabParam}`} key={row.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        closeSidebar();
                      }}
                      isActive={(match, location) => {
                        if (match) return true;
                        return location.pathname.startsWith(`/job-seeker/program/${row.id}`);
                      }}
                    >
                      <CircularLogo logo={row.logo} name={row.name} size={28} wrap={false} />
                      <div className="sub-menu-list-item-title">{row.name}</div>
                    </NavLink>
                  )}
                  wrapList={false}
                  wrapListItems={false}
                  loadingText={<SvgIcon name="spinner" />}
                />
              </NavCollapse>
              <div ref={endRef} className="async-list-end-item" />
            </li>
          )}
          {memberNavSettings?.discoverGroups !== false && (
            <li>
              <NavLink exact to={ROUTES.JOB_SEEKER.EXPLORE_PROGRAMS} onClick={() => closeSidebar()} >
                <span className="menu-link-title"><SvgIcon name="sidebar/programs" />Discover Groups</span>
              </NavLink>
            </li>
          )}
          {isNetworkManager && (onBehalfOfOrganizationAdminId || isProgramOrCommunityLead) &&
            <li>
              <NavCollapse routes={[ROUTES.JOB_SEEKER.PROGRAM_NETWORKS_PAGE, ROUTES.JOB_SEEKER.POST_PROGRAM_NETWORK_MODERATION]} name="Networks" svgIcon="sidebar/networks">
                <>
                  <NavLink className="nav-collapse-link" to={ROUTES.JOB_SEEKER.PROGRAM_NETWORKS_PAGE} onClick={() => closeSidebar()} >
                    <span className="menu-link-title">Group networks</span>
                  </NavLink>
                  <NavLink className="nav-collapse-link" to={ROUTES.JOB_SEEKER.POST_PROGRAM_NETWORK_MODERATION} onClick={() => closeSidebar()} >
                    <span className="menu-link-title">Post moderation</span>
                  </NavLink>
                </>
              </NavCollapse>
            </li>
          }
          <ShowForUserOrMemberRoles roles={[Roles.Owner, Roles.User, MemberRoles.FullAdmin, MemberRoles.ViewOnlyAdmin]} validateOrgnizationContext={true}>
            <li>
              <NavCollapse routes={[ROUTES.MANAGE_ADMINS, ROUTES.ADMIN_MEMBER_GROUPS, ROUTES.ADMIN_MEMBER_PUBLIC_COMMUNITY_SITE_SETTINGS]} name="Admin" svgIcon="sidebar/settings">
                <>
                  <NavLink className="nav-collapse-link" to={ROUTES.MANAGE_ADMINS} onClick={closeSidebar}>
                    <span className="menu-link-title">Manage admins</span>
                  </NavLink>
                  <NavLink className="nav-collapse-link" to={ROUTES.ADMIN_MEMBER_GROUPS} onClick={closeSidebar}>
                    <span className="menu-link-title">Groups</span>
                  </NavLink>
                  <NavLink className="nav-collapse-link" to={ROUTES.ADMIN_MEMBER_PUBLIC_COMMUNITY_SITE_SETTINGS} onClick={closeSidebar}>
                    <span className="menu-link-title">Public Community Site</span>
                  </NavLink>
                </>
              </NavCollapse>
            </li>
          </ShowForUserOrMemberRoles>
        </ul>
      </nav>
    </div >
  );
};

export default JobSeekerAppNavbar;
